import React, { useState, useMemo, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { apiService } from '@/lib/api';
import { Landmark } from 'lucide-react';

/**
 * v2.5.14 — Edit Loan Details form with live-computed Available for Draw.
 *
 * Background:
 * The previous implementation rendered Available for Draw as a free-text
 * input. The user could (and did) edit Total Loan Amount independently
 * without the available field updating, so the Loan Details card stayed
 * stuck on a stale number. We now compute Available for Draw on the fly
 * from `max(0, total_loan − amount_drawn)` and surface it as a read-only
 * display so the relationship is unambiguous. The backend still
 * recomputes server-side as a safety net (server.py L5305-5340).
 */
export default function LoanDetailsEditForm({ project, onSubmit, userId }) {
  const [totalLoan, setTotalLoan] = useState(project?.loan_principal_amount ?? '');
  const [amountDrawn, setAmountDrawn] = useState(project?.amount_drawn ?? '');
  const [interestStart, setInterestStart] = useState(
    project?.interest_start_date ? project.interest_start_date.split('T')[0] : ''
  );
  const [bankName, setBankName] = useState(project?.bank_name ?? '');
  const [loanNumber, setLoanNumber] = useState(project?.loan_number ?? '');
  const [drawAccount, setDrawAccount] = useState(project?.draw_account ?? '');
  const [maturityDate, setMaturityDate] = useState(
    project?.loan_maturity_date ? String(project.loan_maturity_date).split('T')[0] : ''
  );
  const [dayBasis, setDayBasis] = useState(project?.interest_day_basis ?? 360);
  const [lenders, setLenders] = useState([]);
  const [lenderId, setLenderId] = useState(project?.lender_id ?? '');

  useEffect(() => {
    let active = true;
    if (userId) {
      apiService.getLenders(userId)
        .then((res) => { if (active) setLenders(res.data || []); })
        .catch(() => { /* library optional */ });
    }
    return () => { active = false; };
  }, [userId]);

  const handleLenderChange = (id) => {
    setLenderId(id);
    // Convenience: default the bank name to the selected lender if it's empty.
    const picked = lenders.find((l) => l.id === id);
    if (picked && !bankName) setBankName(picked.name);
  };

  const availableForDraw = useMemo(() => {
    const p = parseFloat(totalLoan) || 0;
    const d = parseFloat(amountDrawn) || 0;
    return Math.max(0, p - d);
  }, [totalLoan, amountDrawn]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onSubmit({
      loan_principal_amount: parseFloat(totalLoan) || 0,
      amount_drawn: parseFloat(amountDrawn) || 0,
      loan_available_for_draw: availableForDraw,
      interest_start_date: interestStart ? new Date(interestStart).toISOString() : null,
      loan_maturity_date: maturityDate || null,
      interest_day_basis: parseInt(dayBasis, 10) || 360,
      bank_name: bankName || null,
      loan_number: loanNumber || null,
      draw_account: drawAccount || null,
      lender_id: lenderId || null,
    });
  };

  const fmt = (v) =>
    v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="loan-details-edit-form">
      <div className="space-y-2">
        <Label className="flex items-center gap-1.5"><Landmark className="w-3.5 h-3.5 text-slate-500" /> Lender</Label>
        <select
          value={lenderId}
          onChange={(e) => handleLenderChange(e.target.value)}
          className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
          data-testid="loan-edit-lender-select"
        >
          <option value="">No lender assigned</option>
          {lenders.map((l) => (
            <option key={l.id} value={l.id}>{l.name}</option>
          ))}
        </select>
        <p className="text-xs text-slate-500">
          Assign a bank from your Lenders library to use their bid sheet &amp; draw request form.
        </p>
      </div>
      <div className="space-y-2">
        <Label>Total Loan Amount ($)</Label>
        <Input
          name="loan_principal_amount"
          type="number"
          step="0.01"
          value={totalLoan}
          onChange={(e) => setTotalLoan(e.target.value)}
          data-testid="loan-edit-total-loan-input"
        />
      </div>
      <div className="space-y-2">
        <Label>Amount Drawn ($)</Label>
        <Input
          name="amount_drawn"
          type="number"
          step="0.01"
          value={amountDrawn}
          onChange={(e) => setAmountDrawn(e.target.value)}
          data-testid="loan-edit-amount-drawn-input"
        />
        <p className="text-xs text-slate-500">Interest is calculated on this amount</p>
      </div>
      <div className="space-y-2">
        <Label>Available for Draw ($)</Label>
        <div
          className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-900"
          data-testid="loan-edit-available-for-draw-display"
        >
          <span>{fmt(availableForDraw)}</span>
          <span className="text-[11px] font-normal text-slate-500">
            Auto-calculated · Total Loan − Amount Drawn
          </span>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Interest Start Date</Label>
        <Input
          name="interest_start_date"
          type="date"
          value={interestStart}
          onChange={(e) => setInterestStart(e.target.value)}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Loan Maturity Date</Label>
          <Input
            name="loan_maturity_date"
            type="date"
            value={maturityDate}
            onChange={(e) => setMaturityDate(e.target.value)}
            data-testid="loan-edit-maturity-input"
          />
          <p className="text-[11px] text-slate-500">For projected payoff interest.</p>
        </div>
        <div className="space-y-2">
          <Label>Interest Day-Count</Label>
          <select
            value={dayBasis}
            onChange={(e) => setDayBasis(parseInt(e.target.value, 10))}
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
            data-testid="loan-edit-basis-select"
          >
            <option value={360}>360 (banker&apos;s year)</option>
            <option value={365}>365 (actual)</option>
          </select>
          <p className="text-[11px] text-slate-500">Match your bank (Hills = 360).</p>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Bank Name</Label>
        <Input
          name="bank_name"
          placeholder="e.g., First National Bank"
          value={bankName}
          onChange={(e) => setBankName(e.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label>Loan Number</Label>
        <Input
          name="loan_number"
          value={loanNumber}
          onChange={(e) => setLoanNumber(e.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label>Draw Account</Label>
        <Input
          name="draw_account"
          value={drawAccount}
          onChange={(e) => setDrawAccount(e.target.value)}
        />
      </div>
      <Button type="submit" className="w-full" data-testid="loan-edit-save-btn">
        Save Changes
      </Button>
    </form>
  );
}
